import { useMemo, useState } from "react";
import { C, S } from "../customers/theme";
import ProductPicker from "../products/ProductPicker";
import { computeTotals, PO_STATUSES, PAYMENT_TERMS, CURRENCIES } from "../lib/purchaseOrders";
import { todayISO } from "../lib/dates";

const money = (n, cur = "€") => `${cur}${(Number(n) || 0).toFixed(2)}`;

let _seq = 0;
function Field({ label, value, onChange, type = "text", options, placeholder, autoFocus }) {
  const id = useMemo(() => `po-${++_seq}`, []);
  return (
    <div>
      <label htmlFor={id} style={S.label}>{label}</label>
      {options ? (
        <select id={id} style={S.input} value={value ?? ""} onChange={(e) => onChange(e.target.value)}>
          {options.map((o) => <option key={String(o.value)} value={o.value}>{o.label}</option>)}
        </select>
      ) : (
        <input id={id} style={S.input} type={type} value={value ?? ""} placeholder={placeholder} autoFocus={autoFocus}
          onChange={(e) => onChange(e.target.value)} />
      )}
    </div>
  );
}

const blankLine = () => ({ product_id: null, sku: "", description: "", quantity: 1, unit_cost: 0, discount_percent: 0, vat_rate: 23 });

/**
 * Create/Edit Purchase Order. Delegates persistence to onSave (which calls the
 * purchaseOrders data layer). Receives suppliers + products as props (loaded once
 * by the manager via their data layers) — never touches Supabase directly.
 */
export default function PurchaseOrderForm({ initial, suppliers = [], products = [], onCancel, onSave }) {
  const seed = initial || {};
  const isEdit = !!seed.id;
  const [f, setF] = useState({
    supplier_id: seed.supplier_id || "",
    supplier_contact: seed.supplier_contact || "",
    supplier_email: seed.supplier_email || "",
    order_date: seed.order_date || todayISO(),
    expected_delivery_date: seed.expected_delivery_date || "",
    status: seed.status || "draft",
    preferred_currency: seed.preferred_currency || "EUR",
    payment_terms: seed.payment_terms || "net_30",
    notes: seed.notes || "",
    internal_notes: seed.internal_notes || "",
  });
  const [lines, setLines] = useState(
    (seed.purchase_order_lines && seed.purchase_order_lines.length)
      ? seed.purchase_order_lines.map((l) => ({ ...l }))
      : [blankLine()]
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const supplier = suppliers.find((s) => s.id === f.supplier_id);
  const totals = computeTotals(lines);
  const overdue = f.expected_delivery_date && f.expected_delivery_date < todayISO() && !["received", "cancelled"].includes(f.status);

  const onSupplier = (id) => {
    const s = suppliers.find((x) => x.id === id);
    setF((p) => ({
      ...p,
      supplier_id: id || "",
      supplier_contact: s?.contact_name || p.supplier_contact,
      supplier_email: s?.email || p.supplier_email,
      payment_terms: s?.payment_terms || p.payment_terms,
      preferred_currency: s?.preferred_currency || p.preferred_currency,
    }));
  };

  const updLine = (i, patch) => setLines((p) => p.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  const addLine = () => setLines((p) => [...p, blankLine()]);
  const delLine = (i) => setLines((p) => p.filter((_, j) => j !== i));
  const onPickProduct = (i, prod) => updLine(i, {
    product_id: prod.id, sku: prod.sku || "", description: prod.name,
    unit_cost: prod.cost_price ?? prod.wholesale_price ?? 0, vat_rate: prod.vat_rate ?? 23,
  });

  const submit = async () => {
    setError("");
    if (!f.supplier_id) { setError("Please select a supplier."); return; }
    setBusy(true);
    try {
      await onSave({
        ...f,
        supplier_id: f.supplier_id || null,
        lines: lines.map(({ id, line_total, position, qty_received, ...rest }) => rest),
      });
    } catch (e) {
      setError(e.message || "Could not save purchase order.");
      setBusy(false);
    }
  };

  const cur = f.preferred_currency === "GBP" ? "£" : f.preferred_currency === "USD" ? "$" : "€";

  return (
    <div style={S.modal} onClick={(e) => e.target === e.currentTarget && onCancel()}>
      <div style={{ ...S.modalBox, width: 860 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>
            {isEdit ? "Edit Purchase Order" : "New Purchase Order"}
            {seed.po_number && <span style={{ color: C.textMuted, fontWeight: 500, fontSize: 13, marginLeft: 8, fontFamily: "monospace" }}>{seed.po_number}</span>}
          </h2>
          <button onClick={onCancel} aria-label="Close" style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: C.textMuted }}>✕</button>
        </div>

        <div style={S.grid2}>
          <Field label="Supplier *" value={f.supplier_id} onChange={onSupplier}
            options={[{ value: "", label: "Select supplier…" }, ...suppliers.map((s) => ({ value: s.id, label: `${s.company_name}${s.supplier_number ? ` (${s.supplier_number})` : ""}` }))]} />
          <Field label="Status" value={f.status} onChange={set("status")} options={PO_STATUSES} />
          <Field label="Order Date" type="date" value={f.order_date} onChange={set("order_date")} />
          <Field label="Expected Delivery" type="date" value={f.expected_delivery_date} onChange={set("expected_delivery_date")} />
          <Field label="Currency" value={f.preferred_currency} onChange={set("preferred_currency")} options={CURRENCIES} />
          <Field label="Payment Terms" value={f.payment_terms} onChange={set("payment_terms")} options={PAYMENT_TERMS} />
        </div>

        {supplier && (
          <div style={{ marginTop: 12, background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 14px", fontSize: 12, color: C.textMuted, display: "flex", gap: 18, flexWrap: "wrap" }}>
            <span><strong style={{ color: C.text }}>{supplier.company_name}</strong></span>
            {f.supplier_contact && <span>👤 {f.supplier_contact}</span>}
            {f.supplier_email && <span>✉️ {f.supplier_email}</span>}
            {supplier.phone && <span>☎️ {supplier.phone}</span>}
          </div>
        )}
        {overdue && (
          <div role="alert" style={{ marginTop: 10, background: C.warningDim, border: `1px solid ${C.warning}`, color: C.text, borderRadius: 8, padding: "8px 12px", fontSize: 12, fontWeight: 600 }}>
            ⚠ Expected delivery date is in the past and this order is not yet received.
          </div>
        )}

        <div style={S.sectionTitle}>Line Items</div>
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 64px 90px 56px 56px 84px 28px", gap: 6, marginBottom: 6, fontSize: 11, fontWeight: 700, color: C.textDim }}>
          {["Description", "SKU", "Qty", "Unit Cost", "Disc%", "VAT%", "Line Total", ""].map((h) => <div key={h}>{h}</div>)}
        </div>
        {lines.map((l, i) => (
          <div key={i} style={{ marginBottom: 8 }}>
            <ProductPicker products={products} style={{ marginBottom: 5, width: "100%" }} onSelect={(p) => onPickProduct(i, p)} />
            <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 64px 90px 56px 56px 84px 28px", gap: 6, alignItems: "center" }}>
              <input style={S.input} value={l.description} placeholder="Description" onChange={(e) => updLine(i, { description: e.target.value })} />
              <input style={S.input} value={l.sku || ""} placeholder="SKU" onChange={(e) => updLine(i, { sku: e.target.value })} />
              <input style={S.input} type="number" value={l.quantity} onChange={(e) => updLine(i, { quantity: +e.target.value })} />
              <input style={S.input} type="number" step="0.01" value={l.unit_cost} onChange={(e) => updLine(i, { unit_cost: +e.target.value })} />
              <input style={S.input} type="number" value={l.discount_percent} onChange={(e) => updLine(i, { discount_percent: +e.target.value })} />
              <select style={S.input} value={l.vat_rate} onChange={(e) => updLine(i, { vat_rate: +e.target.value })}>
                {[0, 9, 13.5, 23].map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
              <input style={{ ...S.input, background: C.surface, color: C.textMuted }} value={money(totals.lines[i]?.line_total, cur)} readOnly tabIndex={-1} />
              <button style={{ ...S.btn("ghost"), padding: "4px 6px" }} aria-label="Remove line" onClick={() => delLine(i)}>✕</button>
            </div>
          </div>
        ))}
        <button style={S.btn("secondary")} onClick={addLine}>+ Add Line</button>

        <div style={{ background: C.surface2, borderRadius: 8, padding: 14, margin: "16px 0", maxWidth: 320, marginLeft: "auto" }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}><span style={{ color: C.textMuted }}>Subtotal</span><strong>{money(totals.subtotal, cur)}</strong></div>
          {totals.total_discount > 0 && <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}><span style={{ color: C.textMuted }}>Discount</span><strong>−{money(totals.total_discount, cur)}</strong></div>}
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}><span style={{ color: C.textMuted }}>VAT</span><strong>{money(totals.total_vat, cur)}</strong></div>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 16, fontWeight: 800, borderTop: `1px solid ${C.border}`, paddingTop: 8 }}><span>Total</span><span>{money(totals.total_amount, cur)}</span></div>
        </div>

        <div style={S.grid2}>
          <div><label style={S.label}>Notes (to supplier)</label><textarea style={{ ...S.input, height: 56 }} value={f.notes} onChange={(e) => set("notes")(e.target.value)} /></div>
          <div><label style={S.label}>Internal Notes</label><textarea style={{ ...S.input, height: 56 }} value={f.internal_notes} onChange={(e) => set("internal_notes")(e.target.value)} /></div>
        </div>

        {error && <div style={{ color: C.danger, fontSize: 13, fontWeight: 600, marginTop: 14 }} role="alert">{error}</div>}

        <div style={{ display: "flex", gap: 10, marginTop: 20, justifyContent: "flex-end" }}>
          <button type="button" style={S.btn("secondary")} onClick={onCancel} disabled={busy}>Cancel</button>
          <button type="button" style={{ ...S.btn(), opacity: busy ? 0.6 : 1 }} onClick={submit} disabled={busy}>
            {busy ? "Saving…" : isEdit ? "Save Changes" : "Create Purchase Order"}
          </button>
        </div>
      </div>
    </div>
  );
}
